export class Slideshow extends HTMLElement {
  #current = 0;

  get slides() {
    return Array.from(this.querySelectorAll('slideshow-slide'));
  }

  get current() {
    return this.#current;
  }

  get initialSlide() {
    return this.slides[0];
  }

  get isNested() {
    return false;
  }

  connectedCallback() {
    const initial = Number.parseInt(this.getAttribute('initial-slide') || '0', 10);

    this.select(Number.isNaN(initial) ? 0 : initial, undefined, { animate: false });
    this.addEventListener('pointerdown', this.#handleControlPress, { capture: true });
    this.addEventListener('mousedown', this.#handleControlPress, { capture: true });
    this.addEventListener('touchstart', this.#handleControlPress, { capture: true });
    this.addEventListener('click', this.#handleControlClick, { capture: true });
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#handleControlPress, { capture: true });
    this.removeEventListener('mousedown', this.#handleControlPress, { capture: true });
    this.removeEventListener('touchstart', this.#handleControlPress, { capture: true });
    this.removeEventListener('click', this.#handleControlClick, { capture: true });
  }

  #controlFromEvent(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const node of path) {
      if (node instanceof Element && node.matches('[on\\:click], .slideshow-controls__thumbnail, .slideshow-control--next, .slideshow-control--previous')) return node;
      if (node === this) break;
    }

    const target = event.target instanceof Element ? event.target : null;
    return target?.closest('[on\\:click], .slideshow-controls__thumbnail, .slideshow-control--next, .slideshow-control--previous');
  }

  #actionFromControl(control) {
    const fallback = control.classList.contains('slideshow-control--next')
      ? '/next'
      : control.classList.contains('slideshow-control--previous')
        ? '/previous'
        : '';
    const action = control.getAttribute('on:click') || fallback;
    const match = action.match(/^\/(select|next|previous)(?:\/(-?\d+))?$/);

    if (!match) return null;

    return {
      name: match[1],
      index: match[2] || '0',
    };
  }

  #runControl(control) {
    const action = this.#actionFromControl(control);

    if (!action) return false;

    if (action.name === 'select') {
      this.select(action.index);
    } else if (action.name === 'next') {
      this.next();
    } else {
      this.previous();
    }

    return true;
  }

  #blockCardLink(event) {
    const control = this.#controlFromEvent(event);
    if (!control || !this.contains(control)) return null;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    return control;
  }

  #handleControlPress = (event) => {
    const control = this.#blockCardLink(event);
    if (control?.classList.contains('slideshow-controls__thumbnail')) {
      this.#runControl(control);
    }
  };

  #handleControlClick = (event) => {
    const control = this.#blockCardLink(event);
    if (!control) return;

    this.#runControl(control);
  };

  select(index) {
    const slides = this.slides;

    if (!slides.length) return;

    const targetIndex = Number.parseInt(index, 10);
    if (Number.isNaN(targetIndex)) return;

    const nextIndex = this.hasAttribute('infinite')
      ? (targetIndex + slides.length) % slides.length
      : Math.max(0, Math.min(targetIndex, slides.length - 1));

    slides.forEach((slide, slideIndex) => {
      const isSelected = slideIndex === nextIndex;
      slide.hidden = !isSelected;
      slide.setAttribute('aria-hidden', String(!isSelected));
    });

    this.querySelectorAll('.slideshow-control[aria-selected]').forEach((control, controlIndex) => {
      control.setAttribute('aria-selected', String(controlIndex === nextIndex));
    });

    const currentCounter = this.querySelector('[ref="current"]');
    if (currentCounter) {
      currentCounter.textContent = String(nextIndex + 1);
    }

    this.#current = nextIndex;
    this.dataset.currentSlide = String(nextIndex);
    this.setAttribute('actioned', '');
  }

  next() {
    this.select(this.#current + 1);
  }

  previous() {
    this.select(this.#current - 1);
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}

// manacare-cache-touch: mobile-product-thumbnails-202607231410
